import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faGraduationCap, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { useUserAuth } from "../../context/UserContext.js";
import { getFreshIdToken } from "../../utils/authToken.js";
import { requestMyProject, saveMyProject } from "../../services/userService.js";
import "./MyAppIdeaCard.css";

/**
 * "My App Idea" — the artifact of the course's first lesson (V2101 / V3101 /
 * V4101), and the thing that lesson is graded on.
 *
 * This card is where the idea gets written down. It saves to `users_projects`,
 * and from that moment every later lesson chat is personalized to it — see
 * promptLabClaudeController, which loads the same doc and prepends it to the
 * lesson's system prompt. The rubric reads THIS, not the chat (appIdeaLesson.js).
 *
 * Deliberately four short fields. The offer plan names idea paralysis as the
 * thing that kills week one, so the copy frames app #1 as a practice rep and the
 * card asks for the smallest description that is still useful.
 *
 * Rendered in the middle column by PromptLabLayout when the registry entry sets
 * `captureProject: true`. `showHeading` is false there because the panel header
 * above it already says "My app idea".
 *
 * The card owns the lesson's single action button — save AND submit for grading —
 * because it owns the field state that decides whether either is possible.
 */
const MyAppIdeaCard = ({ onSubmitForGrading, isJudging = false, showHeading = true }) => {
    const { t, i18n } = useTranslation();
    const { user } = useUserAuth();
    const locale = (i18n.language || "en").split("-")[0];

    const [appName, setAppName] = useState("");
    const [whatItDoes, setWhatItDoes] = useState("");
    const [whoItsFor, setWhoItsFor] = useState("");
    const [oneFeature, setOneFeature] = useState("");

    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    // Hydrate from whatever they saved before, so revisiting the lesson (or
    // coming back from CD108) shows their idea rather than an empty form.
    useEffect(() => {
        if (!user?.accessToken) return;
        let cancelled = false;

        (async () => {
            // Fresh token, not the sign-in snapshot: an hour into a session the
            // stale one loads nothing and the student sees an empty form where
            // their saved idea should be.
            const token = await getFreshIdToken(user.accessToken);
            if (cancelled || !token) return;
            const project = await requestMyProject(token);
            if (cancelled || !project) return;
            setAppName(project.appName || "");
            setWhatItDoes(project.idea?.whatItDoes || "");
            setWhoItsFor(project.idea?.whoItsFor || "");
            setOneFeature(project.idea?.oneFeature || "");
        })();

        return () => { cancelled = true; };
    }, [user?.accessToken]);

    // The three fields the lesson's rubric grades (appIdeaLesson.js), which is why
    // "who it's for" is required here where it used to be optional: this card is
    // the graded artifact now, so a card that can be saved must also be a card that
    // can pass. The first feature stays optional and stays ungraded. The token
    // check matters on the free lesson, where the anonymous sign-in that the hub
    // kicks off may not have landed in context yet.
    const canSave =
        !!user?.accessToken &&
        appName.trim().length > 0 &&
        whatItDoes.trim().length > 0 &&
        whoItsFor.trim().length > 0;

    const handleSave = async () => {
        if (!canSave || isSaving) return false;
        setIsSaving(true);
        setError(null);
        try {
            await saveMyProject(
                {
                    appName: appName.trim(),
                    locale,
                    idea: {
                        whatItDoes: whatItDoes.trim(),
                        whoItsFor: whoItsFor.trim(),
                        oneFeature: oneFeature.trim(),
                    },
                },
                await getFreshIdToken(user.accessToken)
            );
            setSaved(true);
            return true;
        } catch (e) {
            setError(e?.message || String(e));
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    // One button, both steps. Saving and grading were two clicks that could only
    // ever be done in one order — the judge reads the SAVED card, so submitting
    // without saving first graded whatever was stored last time. Doing them
    // together removes that trap, and the grade only runs once the save lands.
    const handleSaveAndSubmit = async () => {
        const ok = await handleSave();
        if (ok && onSubmitForGrading) await onSubmitForGrading();
    };

    // Any edit after a save clears the confirmation, so the button always
    // reflects whether what's on screen has actually been stored.
    const onEdit = (setter) => (e) => {
        setter(e.target.value);
        setSaved(false);
    };

    return (
        <div className="my-app-idea">
            {showHeading && (
                <div className="my-app-idea-head">
                    <FontAwesomeIcon icon={faLightbulb} />
                    <strong>{t("promptLab.myAppIdea.title")}</strong>
                </div>
            )}

            <p className="my-app-idea-sub">{t("promptLab.myAppIdea.subtitle")}</p>

            <label className="my-app-idea-label" htmlFor="my-app-idea-name">
                {t("promptLab.myAppIdea.nameLabel")}
            </label>
            <input
                id="my-app-idea-name"
                className="my-app-idea-input"
                type="text"
                value={appName}
                onChange={onEdit(setAppName)}
                placeholder={t("promptLab.myAppIdea.namePlaceholder")}
                maxLength={60}
            />

            <label className="my-app-idea-label" htmlFor="my-app-idea-what">
                {t("promptLab.myAppIdea.whatLabel")}
            </label>
            <textarea
                id="my-app-idea-what"
                className="my-app-idea-input"
                value={whatItDoes}
                onChange={onEdit(setWhatItDoes)}
                placeholder={t("promptLab.myAppIdea.whatPlaceholder")}
                rows={2}
                maxLength={300}
            />

            <label className="my-app-idea-label" htmlFor="my-app-idea-who">
                {t("promptLab.myAppIdea.whoLabel")}
            </label>
            <input
                id="my-app-idea-who"
                className="my-app-idea-input"
                type="text"
                value={whoItsFor}
                onChange={onEdit(setWhoItsFor)}
                placeholder={t("promptLab.myAppIdea.whoPlaceholder")}
                maxLength={120}
            />

            <label className="my-app-idea-label" htmlFor="my-app-idea-feature">
                {t("promptLab.myAppIdea.featureLabel")}
            </label>
            <input
                id="my-app-idea-feature"
                className="my-app-idea-input"
                type="text"
                value={oneFeature}
                onChange={onEdit(setOneFeature)}
                placeholder={t("promptLab.myAppIdea.featurePlaceholder")}
                maxLength={160}
            />

            <div className="my-app-idea-actions">
                <Button
                    type="button"
                    size="sm"
                    className={onSubmitForGrading ? "pl-chat-submit" : undefined}
                    color={onSubmitForGrading ? undefined : "primary"}
                    onClick={onSubmitForGrading ? handleSaveAndSubmit : handleSave}
                    disabled={!canSave || isSaving || isJudging}
                >
                    {onSubmitForGrading && <FontAwesomeIcon icon={faGraduationCap} />}
                    {isSaving
                        ? t("promptLab.myAppIdea.saving")
                        : isJudging
                        ? t("promptLab.grading")
                        : onSubmitForGrading
                        ? t("promptLab.myAppIdea.saveAndSubmit")
                        : t("promptLab.myAppIdea.save")}
                </Button>
                {saved && (
                    <span className="my-app-idea-saved">
                        <FontAwesomeIcon icon={faCheck} /> {t("promptLab.myAppIdea.saved")}
                    </span>
                )}
            </div>

            {error && <div className="my-app-idea-error">{error}</div>}

            <p className="my-app-idea-note">{t("promptLab.myAppIdea.note")}</p>
        </div>
    );
};

export default MyAppIdeaCard;
